import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitUserFeedbackRequest } from 'generated/openapi';
import { TriangleAlert } from 'lucide-react';
import { memo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import CrossedSwords from '@/assets/icons/crossed-swords.svg?react';
import ThinkingFace from '@/assets/icons/thinking-face.svg?react';
import ThumbsUp from '@/assets/icons/thumbs-up.svg?react';
import { analytics } from '@/features/analytics/service';
import { DuoCardsPair } from '@/features/duos/components/duoCardsPair';
import { useGetDuoDetails } from '@/features/duos/hooks/useGetDuoDetails';
import { useSubmitDuoFeedback } from '@/features/duos/hooks/useSubmitDuoFeedback';
import { ReportModal } from '@/features/duos/modals/reportModal';
import { useGetValorantPlayer } from '@/features/games/valorant/hooks/useGetValorantPlayer';
import { useGetMyProfile } from '@/features/profile/hooks/useGetMyProfile';
import { useGetProfile } from '@/features/profile/hooks/useGetProfile';
import { Button } from '@/shared/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
} from '@/shared/components/ui/custom/responsive-dialog';
import { Field, FieldError } from '@/shared/components/ui/field';
import { cn } from '@/shared/utils/cn';
import { getErrorMessage } from '@/shared/utils/errorMessage';

export interface DuoFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

const feedbackSchema = z.object({
  feedbackType: z.enum(['PRAISE', 'SKIP']),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

const titleClassName = 'text-cold-100 text-base font-bold text-center';

export const DuoFeedbackModal = memo(function DuoFeedbackModal(
  props: DuoFeedbackModalProps,
) {
  const { open, onOpenChange, userId } = props;
  const submitFeedback = useSubmitDuoFeedback();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const { data: duoData } = useGetDuoDetails({
    userId,
    options: { select: data => data.duo },
  });

  const { data: duoProfile } = useGetProfile({
    userId,
    options: { select: data => data.profile },
  });

  const { data: myProfile } = useGetMyProfile({
    options: { select: data => data.profile },
  });

  const { data: duoValorantData } = useGetValorantPlayer({
    puuid: duoProfile?.riotPuuid ?? '',
    options: {
      select: data => data.player,
    },
  });

  const { data: myValorantData } = useGetValorantPlayer({
    puuid: myProfile?.riotPuuid ?? '',
    options: {
      select: data => data.player,
    },
  });

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      feedbackType: undefined,
    },
    mode: 'onChange',
  });

  const feedbackType = useWatch({
    control: form.control,
    name: 'feedbackType',
  });
  const isValid = Boolean(feedbackType);

  const handleReportClick = () => {
    onOpenChange(false);
    setIsReportModalOpen(true);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      form.reset();
    }, 300);
  };

  const onSubmit = form.handleSubmit(data => {
    if (data.feedbackType === 'SKIP') {
      onOpenChange(false);
      form.reset();
      return;
    }

    // If not SKIP, must be PRAISE (schema only allows these two values)
    const feedback: SubmitUserFeedbackRequest['feedback'] = {
      kind: 'PRAISE',
    };

    submitFeedback.mutate(
      {
        userId,
        feedback,
      },
      {
        onSuccess: () => {
          toast.success(
            `Nice! you rated your match with ${duoProfile?.displayName ?? 'your partner'}`,
          );
          onOpenChange(false);
          form.reset();
          handleClose();
        },
        onError: async error => {
          const errorMessage = await getErrorMessage(error);
          toast.error(`Failed to submit feedback: ${errorMessage}`);
        },
        onSettled: () => {
          form.reset();
          handleClose();
        },
      },
    );
  });

  if (!duoProfile || !myProfile) {
    return null;
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="bg-cold-950 rounded-t-[28px] md:rounded-lg">
        <div className="flex flex-col gap-6 px-4 pt-6 pb-4">
          <div className="flex justify-center gap-4">
            <DuoCardsPair
              leftRarity={duoProfile.rarity ?? 'COMMON'}
              rightRarity={duoProfile.rarity ?? 'COMMON'}
              leftRank={myValorantData.rank}
              rightRank={duoValorantData.rank}
              leftImage={myProfile.pictureUrl}
              rightImage={duoProfile.pictureUrl}
              leftName={myProfile.displayName}
              rightName={duoProfile.displayName}
              leftIsPremium={myProfile.isPremium}
              rightIsPremium={duoProfile.isPremium}
            />
          </div>

          <div className="flex flex-col items-center gap-2">
            <p className="text-cold-300 flex items-center gap-1.5 text-sm leading-none font-medium">
              <CrossedSwords className="size-5" />
              how is playing with {duoProfile.displayName}?
            </p>
          </div>

          <form
            id="feedback-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-3"
          >
            <Field>
              <Controller
                control={form.control}
                name="feedbackType"
                render={({ field }) => (
                  <div className="flex flex-col gap-3">
                    <Button
                      type="button"
                      onClick={() => {
                        field.onChange('PRAISE');
                      }}
                      className={cn(
                        'h-auto justify-start gap-3 px-5 py-4',
                        field.value === 'PRAISE' && 'bg-positive-950',
                      )}
                      variant="outlinePositive"
                    >
                      <ThumbsUp className="size-6 shrink-0 text-yellow-500" />
                      <div className="flex flex-col items-start gap-2">
                        <p className={titleClassName}>Would play again</p>
                        <p className="text-sm">Good vibes</p>
                      </div>
                    </Button>

                    <Button
                      type="button"
                      onClick={() => {
                        analytics.captureEvent(
                          'Duos Too Soon To Tell Button Clicked',
                          {
                            remote_user_id: userId,
                            duos_level: duoData.progress.level,
                          },
                        );
                        field.onChange('SKIP');
                      }}
                      className={cn(
                        'h-auto justify-start gap-3 px-5 py-4',
                        field.value === 'SKIP' && 'bg-cold-800',
                      )}
                      variant="outline"
                    >
                      <ThinkingFace className="size-6 shrink-0 text-orange-500" />
                      <div className="flex flex-col items-start gap-2">
                        <p className={titleClassName}>Too soon to tell</p>
                        <p className="text-sm">Skip for now</p>
                      </div>
                    </Button>

                    <Button
                      type="button"
                      onClick={handleReportClick}
                      className="h-auto justify-start gap-3 px-5 py-4"
                      variant="outline"
                    >
                      <TriangleAlert
                        className="size-8"
                        fill="#FFB02E"
                        color="black"
                      />
                      <div className="flex flex-col items-start gap-2">
                        <p className={titleClassName}>Report</p>
                        <p className="text-sm">
                          Harassment, cheating or other issues
                        </p>
                      </div>
                    </Button>
                  </div>
                )}
              />
              <FieldError
                error={form.formState.errors.feedbackType}
                mode="dirty"
                isDirty={form.formState.dirtyFields.feedbackType}
              />
            </Field>
          </form>
        </div>

        <ResponsiveDialogFooter className="px-4 pb-6">
          <Button
            type="submit"
            form="feedback-form"
            disabled={!isValid || submitFeedback.isPending}
          >
            {submitFeedback.isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
      <ReportModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        userId={userId}
      />
    </ResponsiveDialog>
  );
});
